import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { Banknote, TrendingUp, Wrench, Download } from "lucide-react";

const COLORS = ["#00E5FF", "#5CEEFF", "#FFA866", "#FFBE8F", "#A3A3A3", "#737373"];

export default function AdminReports() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/reports").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-center py-12 text-neutral-500">Se incarca...</div>;

  const exportCsv = () => {
    const rows = [
      ["Luna", "Numar reparatii", "Venit (RON)"],
      ...data.by_month.map((m) => [m.month, m.count, m.revenue.toFixed(2)]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rapoarte-kukirin-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase">Rapoarte</h1>
          <p className="text-sm text-neutral-400 mt-1">Statistici si analize service</p>
        </div>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold px-5 py-3 text-sm uppercase tracking-wider inline-flex items-center gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <KPI icon={Wrench} label="Reparatii totale" value={data.totals.repairs} />
        <KPI icon={Banknote} label="Venit total" value={`${data.totals.revenue.toFixed(0)} RON`} highlight />
        <KPI icon={TrendingUp} label="Cost mediu / reparatie" value={`${(data.totals.avg_ticket || 0).toFixed(0)} RON`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-neutral-950 border border-neutral-800 p-6">
          <h3 className="font-display text-xl uppercase mb-4">Venit & reparatii / luna</h3>
          {data.by_month.length === 0 ? (
            <div className="text-sm text-neutral-500 py-16 text-center">Nu exista date inca</div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data.by_month}>
                <CartesianGrid stroke="#262626" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#737373" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" stroke="#00E5FF" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#737373" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #00E5FF", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="left" type="monotone" dataKey="revenue" name="Venit (RON)" stroke="#00E5FF" strokeWidth={2} dot={{ fill: "#00E5FF" }} />
                <Line yAxisId="right" type="monotone" dataKey="count" name="Numar reparatii" stroke="#A3A3A3" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-neutral-950 border border-neutral-800 p-6">
          <h3 className="font-display text-xl uppercase mb-4">Status</h3>
          {data.by_status.length === 0 ? (
            <div className="text-sm text-neutral-500 py-16 text-center">Nu exista date</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.status}: ${e.count}`} fontSize={11}>
                  {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #00E5FF", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="lg:col-span-3 bg-neutral-950 border border-neutral-800 p-6">
          <h3 className="font-display text-xl uppercase mb-4">Top modele reparate</h3>
          {data.by_model.length === 0 ? (
            <div className="text-sm text-neutral-500 py-16 text-center">Nu exista date</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.by_model}>
                <CartesianGrid stroke="#262626" strokeDasharray="3 3" />
                <XAxis dataKey="model" stroke="#737373" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis stroke="#737373" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #00E5FF", fontSize: 12 }} />
                <Bar dataKey="count" fill="#00E5FF" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, highlight }) => (
  <div className="bg-neutral-950 border border-neutral-800 p-5">
    <Icon className={`w-6 h-6 mb-3 ${highlight ? "text-[#00E5FF]" : "text-neutral-400"}`} />
    <div className={`font-display text-4xl ${highlight ? "text-[#00E5FF]" : "text-white"}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
  </div>
);
